/* Phillip Nibosi first-visit dialog. No cart hooks, network calls or discount creation. */
(() => {
  if (customElements.get('pn-first-visit-popup')) return;
  const SEEN = 'pn:first-visit:v1';
  const PENDING = 'pn:first-visit:pending:v1';
  const COOKIE = 'pn_first_visit_v1';
  const PENDING_COOKIE = 'pn_first_visit_pending_v1';
  const cookieRead = name => {
    try { return document.cookie.split('; ').find(row => row.startsWith(name + '='))?.slice(name.length + 1) || ''; }
    catch (_) { return ''; }
  };
  const cookieWrite = (name, value, age) => {
    try { document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${age}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; return cookieRead(name) !== ''; }
    catch (_) { return false; }
  };
  const storeRead = key => {
    for (const name of ['localStorage', 'sessionStorage']) {
      try { const value = window[name].getItem(key); if (value) return value; } catch (_) {}
    }
    try { return decodeURIComponent(cookieRead(key === SEEN ? COOKIE : PENDING_COOKIE)); } catch (_) { return ''; }
  };
  const storeWrite = (key, value) => {
    let persistent = false;
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].setItem(key, value); if (name === 'localStorage' && window[name].getItem(key) === value) persistent = true; } catch (_) {}
    }
    return cookieWrite(key === SEEN ? COOKIE : PENDING_COOKIE, value, key === SEEN ? 34560000 : 1800) || persistent;
  };
  const clearPending = () => {
    for (const name of ['localStorage', 'sessionStorage']) { try { window[name].removeItem(PENDING); } catch (_) {} }
    cookieWrite(PENDING_COOKIE, '', 0);
  };
  const visible = el => {
    if (!el || el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
    // Closed details can retain a bounding box although their panel is not painted.
    const closedDetails = el.closest('details:not([open])');
    if (closedDetails && !closedDetails.querySelector(':scope > summary')?.contains(el)) return false;
    const style = getComputedStyle(el), rect = el.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < innerHeight && rect.left < innerWidth;
  };
  class PNFirstVisitPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.form = this.querySelector('form');
      this.formState = this.querySelector('[data-pn-welcome-form-state]')?.dataset.pnWelcomeFormState;
      const pendingAt = Number(storeRead(PENDING));
      this.hasResult = ['success', 'error'].includes(this.formState) && pendingAt > Date.now() - 1800000 && pendingAt <= Date.now();
      this.querySelector('[data-pn-welcome-yes]').addEventListener('click', () => this.step('signup', true), options);
      this.querySelectorAll('[data-pn-welcome-close]').forEach(button => button.addEventListener('click', () => this.close(button.dataset.pnWelcomeClose || 'dismissed'), options));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close('dismissed'); }, options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      this.dialog.addEventListener('close', () => this.unlock(), options);
      // Closing happens only when the pointer both starts and ends on the backdrop.
      const outside = event => { const r = this.dialog.getBoundingClientRect(); return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom; };
      this.dialog.addEventListener('pointerdown', event => { this.backdropDown = event.target === this.dialog && outside(event); }, options);
      this.dialog.addEventListener('click', event => { if (this.backdropDown && event.target === this.dialog && outside(event)) this.close('dismissed'); this.backdropDown = false; }, options);
      this.form.addEventListener('submit', event => {
        if (!this.form.checkValidity()) { event.preventDefault(); this.form.reportValidity(); return; }
        // A marker is not marketing consent: the required, unchecked checkbox is explicit.
        storeWrite(PENDING, String(Date.now()));
      }, options);
      this.querySelector('[data-pn-welcome-copy]')?.addEventListener('click', async () => {
        const input = this.querySelector('#PNWelcomeCode'), status = this.querySelector('[data-pn-welcome-copy-status]');
        try { await navigator.clipboard.writeText(input.value); status.textContent = 'Code copied.'; }
        catch (_) { input.focus(); input.select(); status.textContent = 'Select and copy your code.'; }
      }, options);
      window.addEventListener('pagehide', () => this.close('dismissed', false), options);
      window.addEventListener('storage', event => { if (event.key === SEEN && event.newValue) { this.eligible = false; this.close('dismissed', false); } }, options);
      document.addEventListener('visibilitychange', () => this.scheduleCheck(), options);
      document.addEventListener('focusout', () => this.scheduleCheck(), options);
      // Event driven: wait for competing overlays to finish, without a polling loop.
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.scheduleCheck();
      });
      this.observer.observe(document.body, {subtree:true, childList:true, attributes:true, attributeFilter:['open','class','style','hidden','aria-hidden','aria-expanded']});
      this.timer = setTimeout(() => { this.eligible = true; this.tryOpen(); }, this.hasResult ? 0 : Number(this.dataset.delay || 5000));
    }
    competingOverlay() {
      return [...document.querySelectorAll('dialog[open],[aria-modal="true"],quick-add-modal[open],cart-drawer.active,.menu-drawer-container[open],.pn-mobile-panel.is-open,.an-mobile-menu[open] .an-mobile-panel,.popup-modal--active,#shopify-pc__banner,#shopify-pc__prefs,[data-pn-first-visit-blocker]')]
        .some(el => !this.contains(el) && !el.contains(this) && visible(el));
    }
    scheduleCheck() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.tryOpen(); });
    }
    tryOpen() {
      if (this.dialog.open) {
        if (this.competingOverlay()) this.close('interrupted', false);
        return;
      }
      if (!this.eligible || document.hidden || this.competingOverlay()) return;
      if (!this.hasResult && (storeRead(SEEN) || document.activeElement?.matches('input,textarea,select,[contenteditable="true"]'))) return;
      // Fail closed when durable browser storage is unavailable: never nag each page.
      if (!this.hasResult && !storeWrite(SEEN, 'shown')) { this.eligible = false; return; }
      this.returnFocus = document.activeElement;
      this.step(this.hasResult ? 'signup' : 'offer');
      try { this.dialog.showModal(); } catch (_) { this.eligible = false; return; }
      this.eligible = false;
      this.lock();
      if (this.hasResult) {
        clearPending();
        this.hasResult = false;
        const target = this.querySelector('.pn-welcome__error,.pn-welcome__success');
        target?.focus({preventScroll:true});
      } else this.querySelector('#pn-welcome-title').focus({preventScroll:true});
    }
    step(name, focus = false) {
      this.dialog.dataset.step = name;
      this.querySelectorAll('[data-pn-welcome-step]').forEach(el => { el.hidden = el.dataset.pnWelcomeStep !== name; });
      this.querySelectorAll('[data-pn-welcome-photo]').forEach(el => { el.hidden = el.dataset.pnWelcomePhoto !== name; if (!el.hidden) el.loading = 'eager'; });
      const success = this.formState === 'success' && name === 'signup';
      this.querySelector('#pn-welcome-signup-title').hidden = success;
      this.querySelector('#pn-welcome-signup-description').hidden = success;
      const id = name === 'offer' ? 'pn-welcome-title' : 'pn-welcome-signup-title';
      this.dialog.setAttribute('aria-labelledby', id);
      this.dialog.setAttribute('aria-describedby', name === 'offer' ? 'pn-welcome-description' : 'pn-welcome-signup-description');
      if (success) { this.dialog.removeAttribute('aria-labelledby'); this.dialog.removeAttribute('aria-describedby'); this.dialog.setAttribute('aria-label','Your signup result'); }
      else this.dialog.removeAttribute('aria-label');
      if (focus) { this.dialog.scrollTop = 0; this.querySelector('#pn-welcome-signup-title').focus({preventScroll:true}); }
    }
    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const list = [...this.dialog.querySelectorAll('button:not(:disabled),a[href],input:not([type="hidden"]):not(:disabled),select,textarea,[tabindex="0"]')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      if (!list.length) { event.preventDefault(); return; }
      const index = list.indexOf(document.activeElement);
      if (event.shiftKey && index <= 0) { event.preventDefault(); list.at(-1).focus(); }
      else if (!event.shiftKey && (index === -1 || index === list.length - 1)) { event.preventDefault(); list[0].focus(); }
    }
    lock() {
      this.scrollLock = document.createElement('style');
      this.scrollLock.dataset.pnWelcomeLock = '';
      // Removing only our stylesheet preserves any lock held by another component.
      this.scrollLock.textContent = 'html:has(.pn-welcome[open]),html:has(.pn-welcome[open]) body{overflow:hidden!important;overscroll-behavior:none!important}';
      document.head.append(this.scrollLock);
    }
    unlock() { this.scrollLock?.remove(); this.scrollLock = null; }
    close(reason, restore = true) {
      if (!this.dialog?.open) return;
      this.eligible = false;
      storeWrite(SEEN, reason);
      this.dialog.close();
      this.unlock();
      if (restore && this.returnFocus?.isConnected && !this.competingOverlay()) this.returnFocus.focus({preventScroll:true});
    }
    disconnectedCallback() {
      clearTimeout(this.timer); cancelAnimationFrame(this.frame);
      this.abort?.abort(); this.observer?.disconnect(); this.unlock();
      if (this.dialog?.open) this.dialog.close();
      this.initialized = false;
    }
  }
  customElements.define('pn-first-visit-popup', PNFirstVisitPopup);
})();
